import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { createEshop, updateEshop, getEshop, testEshopConnection, testProductLoading, analyzeLogin } from '../services/eshopService';
import { buildFullScraperConfig } from '../utils/scraperConfig';

const steps = [
    'E-shop Information',
    'Authentication Setup',
    'Selector Configuration',
    'Advanced Settings',
    'Review and Save'
];

export const useEshopWizard = ({ onComplete, eshopId }) => {
    const queryClient = useQueryClient();

    // Main state management
    const [activeStep, setActiveStep] = useState(0);
    const [eshopData, setEshopData] = useState({
        eshop_id: '',
        name: '',
        base_url: '',
        status: 'active',
        currency: 'CZK'
    });
    const [authConfig, setAuthConfig] = useState({
        login_url: '',
        username_field: '',
        password_field: '',
        login_button: '',
        modal_container: '',
        test_credentials: {
            username: '',
            password: ''
        }
    });
    const [advancedSettings, setAdvancedSettings] = useState({
        pagination: {
            next_button: "a[rel='next']",
            page_param: "p",
            max_pages: 50,
        },
        testing: {
            product_limit: 0,
            enabled: false,
        },
        rate_limiting: {
            requests_per_minute: 30,
            delay_between_requests: 100
        },
        retry_settings: {
            max_retries: 3,
            retry_delay: 5000
        },
        enable_lazy_scroll: false,
        ajax_timeout: 10,
        ajax_interval: 50,
        ajax_selectors: '.product-price-ajax,.product-availability-ajax',
        ajax_content_selectors: '.product-price,.product-price-wrapper,.e-pm-price',
        ajax_wait_for_detail_page: false,
        load_more_button_selector: '',
        max_load_more_clicks: 50,
        load_more_timeout: 12,
        scraper_timeout: 7200
    });
    const [categories, setCategories] = useState([
        { name: '', products_url: '', category_default: '' }
    ]);
    const [validationError, setValidationError] = useState(null);

    // Fetch eshop data if editing
    const { data: eshop, isLoading: isEshopLoading } = useQuery(
        ['eshop', eshopId],
        () => getEshop(eshopId).then(res => {
            console.log('[DEBUG] Fetched eshop data from backend:', res.data);
            return res.data;
        }),
        { enabled: !!eshopId }
    );

    // Initialize state from fetched eshop data when editing
    useEffect(() => {
        if (eshop && eshopId) {
            console.log('[DEBUG] useEshopWizard - Initializing state from eshop:', eshop);

            // Initialize eshop data
            setEshopData({
                eshop_id: eshop.eshop_id || '',
                name: eshop.name || '',
                base_url: eshop.base_url || '',
                status: eshop.status || 'active',
                currency: eshop.currency || 'CZK'
            });
            console.log('[DEBUG] Set eshop data:', {
                eshop_id: eshop.eshop_id,
                name: eshop.name,
                base_url: eshop.base_url,
                status: eshop.status
            });

            // Initialize config from config_json
            if (eshop.config_json) {
                console.log('[DEBUG] Initializing from config_json:', eshop.config_json);

                // Initialize auth config
                const authData = {
                    login_url: eshop.config_json.login_url || '',
                    username_field: eshop.config_json.username_field || '',
                    password_field: eshop.config_json.password_field || '',
                    login_button: eshop.config_json.login_button || '',
                    modal_container: eshop.config_json.modal_container || '',
                    test_credentials: eshop.config_json.test_credentials || {
                        username: '',
                        password: ''
                    }
                };
                setAuthConfig(authData);
                console.log('[DEBUG] Set auth config:', authData);

                // Initialize advanced settings
                if (eshop.config_json.advanced) {
                    setAdvancedSettings(eshop.config_json.advanced);
                    console.log('[DEBUG] Set advanced settings:', eshop.config_json.advanced);
                }

                // Initialize categories
                if (eshop.config_json.categories && eshop.config_json.categories.length > 0) {
                    setCategories(eshop.config_json.categories);
                    console.log('[DEBUG] Set categories:', eshop.config_json.categories);
                }
            }
        }
    }, [eshop, eshopId]);

    // Mutations
    const createEshopMutation = useMutation(createEshop, {
        onSuccess: (data) => {
            queryClient.invalidateQueries('eshops');
            if (onComplete) onComplete(data);
        },
        onError: (error) => {
            console.error('Error creating e-shop:', error);
        }
    });

    const updateEshopMutation = useMutation(({ id, ...eshopData }) => updateEshop(id, eshopData), {
        onSuccess: (data) => {
            queryClient.invalidateQueries('eshops');
            queryClient.invalidateQueries(['eshop', eshopId]);
            if (onComplete) onComplete(data);
        },
        onError: (error) => {
            console.error('Error updating e-shop:', error);
        }
    });

    // Separate mutation for saving without triggering onComplete
    const saveEshopMutation = useMutation(({ id, ...eshopData }) => updateEshop(id, eshopData), {
        onSuccess: (data) => {
            queryClient.invalidateQueries('eshops');
            queryClient.invalidateQueries(['eshop', eshopId]);
            // Don't call onComplete - just show success notification
        },
        onError: (error) => {
            console.error('Error saving e-shop:', error);
        }
    });

    const testConnectionMutation = useMutation(({ eshopId, config }) => testEshopConnection(eshopId, config));
    const testProductLoadingMutation = useMutation(({ eshopId, config }) => testProductLoading(eshopId, config));
    const analyzeLoginMutation = useMutation(({ loginUrl, iterations, htmlSize }) => analyzeLogin(loginUrl, iterations, htmlSize));

    const handleNext = (scraperConfig) => {
        // Reset validation error
        setValidationError(null);

        // Add validation checks for moving between steps
        if (activeStep === 0) { // From step 0 to step 1: check e-shop ID, name, base URL
            if (!eshopData.eshop_id || !eshopData.name || !eshopData.base_url) {
                setValidationError('Please fill in all required fields: E-shop ID, Name, and Base URL');
                return;
            }
        } else if (activeStep === 1) { // From step 1 to step 2: check all auth fields
            if (!authConfig.login_url?.trim() || !authConfig.username_field?.trim() || !authConfig.password_field?.trim() || !authConfig.login_button?.trim()) {
                setValidationError('Please fill in all authentication fields: Login URL, Username Field, Password Field, and Login Button. You can use the "Analyze Login" button to automatically detect these.');
                return;
            }
            if (!authConfig.test_credentials?.username?.trim() || !authConfig.test_credentials?.password?.trim()) {
                setValidationError('Please provide test credentials: Username and Password');
                return;
            }
        }

        if (activeStep === steps.length - 1) {
            // Save the e-shop
            const fullConfig = buildFullScraperConfig(scraperConfig, authConfig, advancedSettings, categories);

            const eshopPayload = {
                ...eshopData,
                config_json: fullConfig
            };

            if (eshopId) {
                updateEshopMutation.mutate({ id: eshopId, ...eshopPayload });
            } else {
                createEshopMutation.mutate(eshopPayload);
            }
        } else {
            setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => {
        setActiveStep(activeStep - 1);
    };

    const jumpToStep = (stepIndex) => {
        if (stepIndex >= 0 && stepIndex < steps.length) {
            setActiveStep(stepIndex);
        }
    };

    return {
        // State
        activeStep,
        setActiveStep,
        eshopData,
        setEshopData,
        authConfig,
        setAuthConfig,
        advancedSettings,
        setAdvancedSettings,
        categories,
        setCategories,
        validationError,
        setValidationError,

        // Queries
        eshop,
        isEshopLoading,

        // Mutations
        createEshopMutation,
        updateEshopMutation,
        saveEshopMutation,
        testConnectionMutation,
        testProductLoadingMutation,
        analyzeLoginMutation,

        // Functions
        handleNext,
        handleBack,
        jumpToStep,
        steps
    };
};